// Shared note + star-rating logic for the Team Directory and the Vendor
// Directory. Notes are attributed (author + date stamped at write time);
// only the author — or the Team Lead — may edit or delete one. Ratings are
// one-per-person: a map of userId -> 1..5 stars, so re-rating overwrites
// your old vote instead of stacking a second one.
//
// Everything here is pure so it can be unit tested without Firestore. The
// Firestore writes live in utils/vendorDirectory.js (vendors) and
// utils/directoryNotes.js (Team Directory contacts).

export function personName(user) {
  if (!user) return "";
  const full = `${user.firstname || ""} ${user.lastname || ""}`.trim();
  return full || user.name || user.email || "";
}

export function userDisplayName(users, id) {
  if (!id) return "";
  const u = (users || []).find((x) => x && x.id === id);
  return u ? personName(u) : "";
}

// Stamps a note with its author at the moment it's written, so the name
// sticks even if the user is later renamed or removed.
export function buildNote(text, user, now = Date.now()) {
  return {
    id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    text: String(text || "").trim(),
    authorId: (user && user.id) || "",
    authorName: personName(user),
    createdAt: now,
  };
}

// Newest attributed notes first. Any legacy unattributed `notes` string is
// kept at the bottom (read-only) so nothing written before this feature is
// lost. Each entry keeps its identity (id when stamped, else author+time+
// text) so edits and deletes address the exact note even if the stored
// array changes underneath the open page — never its position, which shifts
// whenever someone else adds or deletes.
export function noteList(entries, legacyNotes) {
  const raw = Array.isArray(entries) ? entries : [];

  const out = raw
    .filter((n) => n && String(n.text || "").trim())
    .map((n) => ({
      id: n.id || "",
      text: String(n.text).trim(),
      authorId: n.authorId || "",
      authorName: n.authorName || "",
      createdAt: n.createdAt || 0,
    }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const legacy = typeof legacyNotes === "string" ? legacyNotes.trim() : "";
  if (legacy) {
    out.push({
      id: "",
      text: legacy,
      authorId: "",
      authorName: "",
      createdAt: 0,
      legacy: true,
    });
  }
  return out;
}

// The identity used to find a note again at save time. Notes written since
// this feature carry a unique id; older attributed notes are matched by
// author + timestamp + text, which is as exact as their data allows.
export function noteRef(note) {
  if (!note) return null;
  return {
    id: note.id || "",
    authorId: note.authorId || "",
    createdAt: note.createdAt || 0,
    text: String(note.text || ""),
  };
}

export function entryMatchesRef(entry, ref) {
  if (!entry || !ref) return false;
  if (ref.id && entry.id) return entry.id === ref.id;
  return (
    (entry.authorId || "") === ref.authorId &&
    (entry.createdAt || 0) === ref.createdAt &&
    String(entry.text || "").trim() === ref.text.trim()
  );
}

// A note can be managed by its author or by the Team Lead. Legacy notes
// (no author recorded) are read-only for everyone — there's no way to know
// whose words they are.
export function canManageNote(note, user) {
  if (!note || !user || note.legacy || !note.authorId) return false;
  return note.authorId === user.id || user.role === "Admin";
}

// New array with the referenced note rewritten, or null when the note no
// longer exists (someone else deleted it first) — callers must treat null
// as "nothing to save" and refresh, never write it. Empty replacement text
// also returns null: deleting is a separate, deliberate action.
export function replaceNoteByRef(entries, ref, newText) {
  const raw = Array.isArray(entries) ? entries : [];
  const text = String(newText || "").trim();
  const idx = raw.findIndex((n) => entryMatchesRef(n, ref));
  if (!text || idx === -1) return null;
  return raw.map((n, i) => (i === idx ? { ...n, text } : n));
}

// New array without the referenced note, or null when it's already gone.
export function removeNoteByRef(entries, ref) {
  const raw = Array.isArray(entries) ? entries : [];
  const idx = raw.findIndex((n) => entryMatchesRef(n, ref));
  if (idx === -1) return null;
  return raw.filter((_, i) => i !== idx);
}

// Ratings map -> { average, count }. Ignores anything that isn't a 1..5
// number so a malformed write can never poison the average.
export function ratingSummary(ratings) {
  const values = Object.values(ratings || {}).filter(
    (v) => typeof v === "number" && v >= 1 && v <= 5,
  );
  if (!values.length) return { average: 0, count: 0 };
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  return { average: Math.round(avg * 10) / 10, count: values.length };
}

// The signed-in user's own vote, or 0 if they haven't rated yet.
export function myRating(ratings, userId) {
  const v = ratings && userId ? ratings[userId] : 0;
  return typeof v === "number" && v >= 1 && v <= 5 ? v : 0;
}
